import {
  Component,
  OnInit,
  inject,
  signal,
  computed,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { CatalogueApiService, UserInteractionApiService } from '@angular-workspace/services';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import {
  DetailHeaderComponent,
  DetailHeaderConfig,
} from '../../../shared/components/detail-header/detail-header.component';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { HelperService } from '../../../shared/services/helper.service';
import { FormatService } from '../../../shared/services/format.service';
import { ToasterService } from '../../../services/toaster.service';
import { environment } from '../../../../environments/environment';
import type { DatasetDetailTabId } from './dataset-detail-tab.model';
import { SkeletonComponent } from '../../../shared/components/skeleton/skeleton.component';
import { parseBookmarkLikeFromUserInteractions } from '../../../shared/utils/user-interaction-bookmark-like';
import {
  DatasetDetailDatasetTabComponent,
  type DatasetResourceServer,
} from '../dataset/dataset-tab.component';
import { DatasetDetailDetailsTabComponent } from '../details/details-tab.component';
import { DatasetDetailVisualisationTabComponent } from '../visualisation/visualisation-tab.component';
import { DatasetDetailSqlPlaygroundTabComponent } from '../sql-playground/sql-playground-tab.component';

@Component({
  selector: 'app-dataset-details-home',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    FooterComponent,
    DetailHeaderComponent,
    LoginModalComponent,
    DatasetDetailDatasetTabComponent,
    DatasetDetailDetailsTabComponent,
    DatasetDetailVisualisationTabComponent,
    DatasetDetailSqlPlaygroundTabComponent,
    SkeletonComponent,
  ],
  templateUrl: './dataset-details-home.html',
  styleUrl: './dataset-details-home.scss',
})
export class DatasetDetailsHome implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly userInteractionApi = inject(UserInteractionApiService);
  private readonly keycloak = inject(KeycloakService);
  private readonly helper = inject(HelperService);
  private readonly formatService = inject(FormatService);
  private readonly toaster = inject(ToasterService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly activeTab = signal<DatasetDetailTabId>('dataset');
  readonly loading = signal(true);
  readonly showLoginModal = signal(false);
  readonly isLoggedIn = signal(false);
  readonly isBookmarked = signal(false);
  readonly isLiked = signal(false);

  datasetId: string | null = null;

  headerConfig: DetailHeaderConfig = {
    title: '',
    shortDescription: '',
    updatedAt: '',
    breadcrumbs: [],
  };

  datasetDescription = '';
  datasetTags: string[] = [];
  sampleData: unknown = null;
  viewCodeText = '';
  getDataSnippet = '';

  /** Raw catalogue item for resource-server / file detection in dataset tab actions. */
  fullDatasetPayload: Record<string, unknown> | null = null;
  /** Normalized `resourceServer` array from the catalogue item. */
  resourceServersList: DatasetResourceServer[] = [];

  activityMetrics = {
    likes: 0,
    views: 0,
    downloads: 0,
  };

  private detailState = signal<Record<string, string>>({});

  readonly detailRows = computed(() => this.detailState());

  async ngOnInit(): Promise<void> {
    this.isLoggedIn.set(await this.keycloak.isLoggedIn());
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      this.datasetId = id;
      if (id) {
        void this.loadDatasetDetails(id);
      } else {
        this.loading.set(false);
        this.toaster.error('Missing dataset id');
      }
    });
  }

  isLockedTab(tab: DatasetDetailTabId): boolean {
    return tab === 'details' || tab === 'visualisation' || tab === 'sql-playground';
  }

  onTabClick(tab: DatasetDetailTabId): void {
    if (this.isLockedTab(tab) && !this.isLoggedIn()) {
      this.showLoginModal.set(true);
      return;
    }
    this.activeTab.set(tab);
  }

  onLoginModalClose(): void {
    this.showLoginModal.set(false);
    void this.keycloak.isLoggedIn().then((v) => {
      this.isLoggedIn.set(v);
      if (v && this.datasetId) {
        void this.refreshUserInteractionState(this.datasetId);
      }
    });
  }

  async onHeaderBookmarkClick(): Promise<void> {
    const id = this.datasetId;
    if (!id) return;
    try {
      const actionType = this.isBookmarked() ? 'UNBOOKMARK' : 'BOOKMARK';
      await this.userInteractionApi.createBookmarkOrLike({
        assetId: id,
        assetType: 'DATABANK',
        actionType,
      });
      this.isBookmarked.update((v) => !v);
      this.toaster.success(
        this.isBookmarked()
          ? "You've bookmarked the dataset successfully."
          : 'Bookmark removed.',
      );
    } catch (e) {
      console.error(e);
      this.toaster.error('Failed to update bookmark');
    }
  }

  async onHeaderLikeClick(): Promise<void> {
    const id = this.datasetId;
    if (!id) return;
    try {
      const actionType = this.isLiked() ? 'DISLIKE' : 'LIKE';
      await this.userInteractionApi.createBookmarkOrLike({
        assetId: id,
        assetType: 'DATABANK',
        actionType,
      });
      const nextLiked = !this.isLiked();
      this.isLiked.set(nextLiked);
      this.activityMetrics.likes = Math.max(
        0,
        (this.activityMetrics.likes ?? 0) + (nextLiked ? 1 : -1),
      );
      this.toaster.success(
        nextLiked ? "You've liked the dataset successfully." : 'Like removed.',
      );
    } catch (e) {
      console.error(e);
      this.toaster.error('Failed to update like');
    }
  }

  async onDatasetTabAuthRefresh(): Promise<void> {
    this.isLoggedIn.set(await this.keycloak.isLoggedIn());
  }

  private formatNumericDate(iso: string): string {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '—';
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    } catch {
      return '—';
    }
  }

  /** Normalise industry / domain from catalogue item (string or array). */
  private formatIndustryField(raw: unknown): string {
    if (raw == null) return '—';
    if (Array.isArray(raw)) {
      const parts = raw
        .filter((v) => v !== null && v !== undefined && v !== '')
        .map((v) => String(v));
      return parts.length ? parts.join(', ') : '—';
    }
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    return '—';
  }

  private pickVerifiedBy(datasetData: Record<string, unknown>): string {
    const candidates: unknown[] = [
      datasetData['verifiedBy'],
      datasetData['verified_by'],
      datasetData['verifier'],
      datasetData['verifierName'],
      datasetData['cosName'],
      datasetData['cos'],
    ];
    for (const c of candidates) {
      if (c == null) continue;
      if (typeof c === 'string' && c.trim()) return c.trim();
      if (typeof c === 'object' && c !== null && 'name' in c) {
        const n = (c as { name?: unknown }).name;
        if (typeof n === 'string' && n.trim()) return n.trim();
      }
    }
    return '—';
  }

  private pickOrganizationType(datasetData: Record<string, unknown>): string {
    const root =
      datasetData['organizationType'] ??
      datasetData['organisationType'] ??
      datasetData['organization_type'] ??
      datasetData['organisation_type'];
    if (root != null && typeof root === 'string' && root.trim()) {
      return root.trim();
    }
    const nested = datasetData['organisation'] ?? datasetData['organization'];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const o = nested as Record<string, unknown>;
      const t =
        o['organisationType'] ??
        o['organizationType'] ??
        o['type'];
      if (t != null && typeof t === 'string' && t.trim()) return t.trim();
    }
    return '—';
  }

  /** Align with dataset cards: organisation name from string or nested org object. */
  private pickOrganisationName(
    datasetData: Record<string, unknown>,
    publisher: string,
  ): string {
    const s = (v: unknown) =>
      typeof v === 'string' && v.trim() ? v.trim() : '';
    const fromRoot =
      s(datasetData['organizationName']) ||
      s(datasetData['organisationName']) ||
      s(datasetData['organisation']) ||
      s(datasetData['organization']);
    if (fromRoot) return fromRoot;
    const nested = datasetData['organisation'] ?? datasetData['organization'];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const o = nested as Record<string, unknown>;
      const n =
        s(o['name']) ||
        s(o['organisationName']) ||
        s(o['organizationName']);
      if (n) return n;
    }
    return publisher || '—';
  }

  private formatRelativeUpdated(iso: string): string {
    if (!iso) return '—';
    try {
      const normalized = iso.trim();
      const t = new Date(
        /[Zz]$|[+-]\d{2}:?\d{2}$/.test(normalized)
          ? normalized
          : normalized.replace(/\.\d+$/, '') + 'Z',
      ).getTime();
      if (Number.isNaN(t)) return `Updated ${this.formatService.formatDateForCard(iso)}`;
      const diff = Date.now() - t;
      const sec = Math.floor(diff / 1000);
      if (sec < 45) return 'Updated just now';
      const min = Math.floor(sec / 60);
      if (min < 60) return `Updated ${min} minute${min === 1 ? '' : 's'} ago`;
      const h = Math.floor(min / 60);
      if (h < 24) return `Updated ${h} hour${h === 1 ? '' : 's'} ago`;
      const d = Math.floor(h / 24);
      if (d < 14) return `Updated ${d} day${d === 1 ? '' : 's'} ago`;
      return `Updated ${this.formatService.formatDateForCard(iso)}`;
    } catch {
      return '—';
    }
  }

  private extractItem(response: unknown): Record<string, unknown> | null {
    if (!response || typeof response !== 'object') return null;
    const r = response as Record<string, unknown>;
    let data: unknown = r;
    if (r['result'] != null) {
      const res = r['result'];
      data = Array.isArray(res) ? res[0] : res;
    } else if (r['data'] != null) {
      const res = r['data'];
      data = Array.isArray(res) ? res[0] : res;
    } else if (Array.isArray(r['items']) && (r['items'] as unknown[]).length > 0) {
      data = (r['items'] as unknown[])[0];
    } else if (Array.isArray(response) && (response as unknown[]).length > 0) {
      data = (response as unknown[])[0];
    }
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  }

  private async loadDatasetDetails(id: string): Promise<void> {
    this.loading.set(true);
    this.isBookmarked.set(false);
    this.isLiked.set(false);
    try {
      const response = await this.catalogueApi.getItemById(id);
      const datasetData = this.extractItem(response);

      if (!datasetData) {
        this.toaster.error('Could not load dataset');
        this.loading.set(false);
        return;
      }

      this.fullDatasetPayload = datasetData;
      const rsRaw = datasetData['resourceServer'];
      this.resourceServersList = Array.isArray(rsRaw)
        ? (rsRaw as DatasetResourceServer[])
        : rsRaw != null
          ? [rsRaw as DatasetResourceServer]
          : [];

      const title =
        (datasetData['label'] as string) ||
        (datasetData['name'] as string) ||
        'Dataset';

      const publisher =
        (datasetData['provider'] as string) ||
        (datasetData['organisation'] as string) ||
        (datasetData['organization'] as string) ||
        '';

      const headerShortDescription =
        String(datasetData['shortDescription'] ?? '').trim() ||
        String(datasetData['description'] ?? '').trim() ||
        String(datasetData['summary'] ?? '').trim() ||
        '';

      const lastUpdatedRaw =
        (datasetData['lastUpdated'] as string) ||
        (datasetData['itemUpdatedAt'] as string) ||
        (datasetData['itemCreatedAt'] as string) ||
        (datasetData['createdAt'] as string) ||
        '';

      this.headerConfig = {
        title,
        shortDescription: headerShortDescription,
        updatedAt: this.formatRelativeUpdated(lastUpdatedRaw),
        breadcrumbs: [
          { label: 'Datasets', route: '/datasets' },
          { label: title },
        ],
        assetId: id,
      };

      this.datasetDescription =
        (datasetData['description'] as string) ||
        (datasetData['shortDescription'] as string) ||
        (datasetData['summary'] as string) ||
        '';

      this.datasetTags = [];
      const tags = datasetData['tags'] ?? datasetData['tag'];
      if (Array.isArray(tags)) {
        this.datasetTags = tags.map((t) => String(t));
      }

      let yearRange = '';
      const tc = datasetData['temporalCoverage'];
      if (typeof tc === 'string') {
        yearRange = tc;
      } else if (tc && typeof tc === 'object') {
        const o = tc as Record<string, string>;
        if (o['start'] && o['end']) {
          const sy = new Date(o['start']).getFullYear();
          const ey = new Date(o['end']).getFullYear();
          yearRange = `${sy}–${ey}`;
        }
      } else if (datasetData['yearRange']) {
        yearRange = String(datasetData['yearRange']);
      }

      const permission = this.helper.extractAccessBadge(datasetData) || 'Public';
      const createdAt = this.formatNumericDate(
        (datasetData['itemCreatedAt'] as string) ||
          (datasetData['createdAt'] as string) ||
          '',
      );
      const orgName = this.pickOrganisationName(datasetData, publisher);

      const industryStr = this.formatIndustryField(datasetData['industry']);
      const domainFallback =
        typeof datasetData['domain'] === 'string'
          ? datasetData['domain'].trim()
          : '';
      const industryDisplay =
        industryStr !== '—' ? industryStr : domainFallback || '—';

      const verifiedBy = this.pickVerifiedBy(datasetData);
      const organizationType = this.pickOrganizationType(datasetData);
      const updateFrequency =
        (datasetData['uploadFrequency'] as string) ||
        (datasetData['updateFrequency'] as string) ||
        '—';
      const fileFormat =
        (datasetData['fileFormat'] as string) ||
        (datasetData['format'] as string) ||
        '';
      const license =
        (datasetData['license'] as string) ||
        (datasetData['licence'] as string) ||
        '—';

      const lastUpdatedDisplay = lastUpdatedRaw
        ? this.formatService.formatDateForCard(lastUpdatedRaw)
        : '—';

      this.detailState.set({
        datasetTitle: title,
        datasetPermission: permission,
        organisationName: orgName,
        organizationType,
        verifiedBy,
        industry: industryDisplay,
        createdAt,
        publisher: publisher || '—',
        updateFrequency,
        yearRange: yearRange || '—',
        lastUpdated: lastUpdatedDisplay,
        license,
        fileFormat,
      });

      const metrics = (datasetData['metrics'] as Record<string, number>) || datasetData;
      this.activityMetrics = {
        likes:
          (metrics['likes'] as number) ??
          (metrics['likeCount'] as number) ??
          (metrics['like_count'] as number) ??
          0,
        views:
          (metrics['views'] as number) ??
          (metrics['viewCount'] as number) ??
          (metrics['view_count'] as number) ??
          0,
        downloads:
          (metrics['downloads'] as number) ??
          (metrics['downloadCount'] as number) ??
          (metrics['download_count'] as number) ??
          0,
      };

      const rawSample = datasetData['dataSample'] ?? datasetData['data_sample'];
      if (rawSample != null) {
        if (typeof rawSample === 'string') {
          try {
            this.sampleData = JSON.parse(rawSample);
          } catch {
            this.sampleData = rawSample;
          }
        } else {
          this.sampleData = rawSample;
        }
      } else {
        this.sampleData = null;
      }

      this.viewCodeText = this.generateViewCode(this.resourceServersList, id);

      const rsBase = environment.baseRsPathUrl?.replace(/\/?$/, '/') ?? '';
      this.getDataSnippet = `${rsBase}ngsi-ld/v2/${id}/temporal/entities\n\nUse your access token and the catalogue resource server context to query this dataset.`;

      if (this.isLoggedIn()) {
        void this.refreshUserInteractionState(id);
      }
    } catch (e) {
      console.error(e);
      this.toaster.error('Failed to load dataset');
    } finally {
      this.loading.set(false);
      this.cdr.markForCheck();
    }
  }

  private async refreshUserInteractionState(assetId: string): Promise<void> {
    try {
      const response = await this.userInteractionApi.userInteractions({
        assetId,
        assetType: 'DATABANK',
      });
      const { bookmarked, liked } =
        parseBookmarkLikeFromUserInteractions(response);
      this.isBookmarked.set(bookmarked);
      this.isLiked.set(liked);
    } catch {
      // Silent — metrics already from catalogue item API
    }
  }

  private controlPlaneTokenUrl(): string {
    const base = environment.basePathUrl.replace(/\/+$/, '');
    const auth = environment.authApiPath.replace(/^\/+/, '').replace(/\/+$/, '');
    return `${base}/${auth}/token`;
  }

  private generateViewCode(
    resourceServers: DatasetResourceServer[],
    datasetId: string,
  ): string {
    const servers = resourceServers ?? [];
    const upperNames = servers.map((s) => (s.name ?? '').toUpperCase());
    const tokenUrl = this.controlPlaneTokenUrl();
    const findFirstUrl = (match: (n: string) => boolean): string => {
      const rs = servers.find((s) => match((s.name ?? '').toUpperCase()));
      const u = rs?.url?.trim();
      if (u) return u.replace(/\/+$/, '');
      return '';
    };

    if (upperNames.some((n) => n.includes('FILE'))) {
      const fileBase = `${environment.baseFilePathUrl.replace(/\/+$/, '')}/`;
      return this.pythonFileServerExample(tokenUrl, fileBase, datasetId);
    }
    if (upperNames.some((n) => n.includes('OGC'))) {
      const ogc =
        findFirstUrl((n) => n.includes('OGC')) ||
        environment.api_ogc_server_domain.replace(/\/+$/, '');
      return this.pythonOgcExample(tokenUrl, ogc, datasetId);
    }
    if (upperNames.some((n) => n.includes('GATEWAY'))) {
      const gw =
        findFirstUrl((n) => n.includes('GATEWAY')) ||
        environment.gatewayApiPath.replace(/\/+$/, '');
      return this.pythonGatewayExample(tokenUrl, gw, datasetId);
    }
    const rsBase =
      findFirstUrl(() => true) ||
      environment.baseRsPathUrl.replace(/\/+$/, '');
    return this.pythonNgsiLdExample(tokenUrl, rsBase, datasetId);
  }

  private pythonNgsiLdExample(
    controlPlaneTokenUrl: string,
    rsBaseUrl: string,
    datasetId: string,
  ): string {
    const entitiesUrl = `${rsBaseUrl}/ngsi-ld/v2/entities/${datasetId}`;
    return [
      '#!/usr/bin/env python3',
      '"""Fetch paginated NGSI-LD entity data using a token from the Control Plane."""',
      '',
      'import requests',
      'import json',
      '',
      `CONTROL_PLANE_URL = "${controlPlaneTokenUrl}"`,
      `ENTITIES_URL = "${entitiesUrl}"`,
      'CLIENT_ID = "Please use your client details"',
      'CLIENT_SECRET = "Please use your client details"',
      'TIMEOUT = 30',
      '',
      '',
      'def get_access_token():',
      '    headers = {"clientId": CLIENT_ID, "clientSecret": CLIENT_SECRET}',
      '    resp = requests.post(CONTROL_PLANE_URL, headers=headers, timeout=TIMEOUT)',
      '    resp.raise_for_status()',
      '    data = resp.json()',
      '    token = data.get("result", {}).get("access_token")',
      '    if not token:',
      '        raise ValueError("access_token not found in response")',
      '    return token',
      '',
      '',
      'def fetch_entities_paginated(access_token):',
      '    headers = {"Authorization": f"Bearer {access_token}"}',
      '    all_data = []',
      '    page = 1',
      '    first_url = f"{ENTITIES_URL}?page={page}"',
      '    resp = requests.get(first_url, headers=headers, timeout=TIMEOUT)',
      '    resp.raise_for_status()',
      '    data = resp.json()',
      '    all_data.append(data)',
      '    pagination_info = data.get("paginationInfo", {})',
      '    total_pages = pagination_info.get("totalPages", 1)',
      '    for page in range(2, total_pages + 1):',
      '        url = f"{ENTITIES_URL}?page={page}"',
      '        resp = requests.get(url, headers=headers, timeout=TIMEOUT)',
      '        resp.raise_for_status()',
      '        all_data.append(resp.json())',
      '    return all_data',
      '',
      '',
      'if __name__ == "__main__":',
      '    token = get_access_token()',
      '    pages = fetch_entities_paginated(token)',
      '    with open("ngsi-ld-response.json", "w", encoding="utf-8") as f:',
      '        json.dump(pages, f, indent=2)',
      '    print("Saved ngsi-ld-response.json")',
    ].join('\n');
  }

  private pythonFileServerExample(
    controlPlaneTokenUrl: string,
    fileBaseUrl: string,
    datasetId: string,
  ): string {
    const filesUrl = `${fileBaseUrl}v1/databanks/${datasetId}/files`;
    return [
      '#!/usr/bin/env python3',
      '"""List files for this databank on the File Server using a Control Plane token."""',
      '',
      'import requests',
      'import json',
      '',
      `CONTROL_PLANE_URL = "${controlPlaneTokenUrl}"`,
      `FILE_SERVER_URL = "${filesUrl}"`,
      'CLIENT_ID = "Please use your client details"',
      'CLIENT_SECRET = "Please use your client details"',
      'TIMEOUT = 30',
      '',
      '',
      'def get_access_token():',
      '    headers = {"clientId": CLIENT_ID, "clientSecret": CLIENT_SECRET}',
      '    resp = requests.post(CONTROL_PLANE_URL, headers=headers, timeout=TIMEOUT)',
      '    resp.raise_for_status()',
      '    data = resp.json()',
      '    token = data.get("result", {}).get("access_token")',
      '    if not token:',
      '        raise ValueError("access_token not found in response")',
      '    return token',
      '',
      '',
      'def list_files(access_token):',
      '    headers = {',
      '        "Authorization": f"Bearer {access_token}",',
      '        "Content-Type": "application/json",',
      '    }',
      '    payload = {"maxKeys": 1000, "recursive": True}',
      '    resp = requests.post(FILE_SERVER_URL, headers=headers, json=payload, timeout=TIMEOUT)',
      '    resp.raise_for_status()',
      '    return resp.json()',
      '',
      '',
      'if __name__ == "__main__":',
      '    token = get_access_token()',
      '    data = list_files(token)',
      '    with open("files-response.json", "w", encoding="utf-8") as f:',
      '        json.dump(data, f, indent=2)',
      '    print("Saved files-response.json")',
    ].join('\n');
  }

  private pythonOgcExample(
    controlPlaneTokenUrl: string,
    ogcBaseUrl: string,
    datasetId: string,
  ): string {
    const itemsUrl = `${ogcBaseUrl}/collections/${datasetId}/items`;
    return [
      '#!/usr/bin/env python3',
      '"""Fetch OGC Features items for this dataset collection."""',
      '',
      'import requests',
      'import json',
      '',
      `CONTROL_PLANE_URL = "${controlPlaneTokenUrl}"`,
      `OGC_ITEMS_URL = "${itemsUrl}"`,
      '# Adjust bbox and CRS for your area of interest',
      'OGC_QUERY = "?bbox=67.97,23.03,77.87,31.82&bbox-crs=http://www.opengis.net/def/crs/OGC/1.3/CRS84"',
      'CLIENT_ID = "Please use your client details"',
      'CLIENT_SECRET = "Please use your client details"',
      'TIMEOUT = 30',
      '',
      '',
      'def get_access_token():',
      '    headers = {"clientId": CLIENT_ID, "clientSecret": CLIENT_SECRET}',
      '    resp = requests.post(CONTROL_PLANE_URL, headers=headers, timeout=TIMEOUT)',
      '    resp.raise_for_status()',
      '    data = resp.json()',
      '    token = data.get("result", {}).get("access_token")',
      '    if not token:',
      '        raise ValueError("access_token not found in response")',
      '    return token',
      '',
      '',
      'if __name__ == "__main__":',
      '    token = get_access_token()',
      '    url = OGC_ITEMS_URL + OGC_QUERY',
      '    headers = {"authorization": f"Bearer {token}"}',
      '    resp = requests.get(url, headers=headers, timeout=TIMEOUT)',
      '    resp.raise_for_status()',
      '    out = resp.json()',
      '    with open("ogc-response.json", "w", encoding="utf-8") as f:',
      '        json.dump(out, f, indent=2)',
      '    print("Saved ogc-response.json")',
    ].join('\n');
  }

  private pythonGatewayExample(
    controlPlaneTokenUrl: string,
    gatewayBaseUrl: string,
    datasetId: string,
  ): string {
    const rsBase = `${gatewayBaseUrl}/rsp/ngsi-ld/v1/entities`;
    return [
      '#!/usr/bin/env python3',
      '"""Query the Gateway (RS proxy) NGSI-LD API for this dataset entity id."""',
      '',
      'import requests',
      'import json',
      'import urllib.parse',
      '',
      `CONTROL_PLANE_URL = "${controlPlaneTokenUrl}"`,
      `RS_BASE_URL = "${rsBase}"`,
      `ENTITY_ID = "${datasetId}"`,
      'CLIENT_ID = "Please use your client details"',
      'CLIENT_SECRET = "Please use your client details"',
      'TIMEOUT = 30',
      '',
      '',
      'def get_access_token():',
      '    headers = {"clientId": CLIENT_ID, "clientSecret": CLIENT_SECRET}',
      '    resp = requests.post(CONTROL_PLANE_URL, headers=headers, timeout=TIMEOUT)',
      '    resp.raise_for_status()',
      '    data = resp.json()',
      '    token = data.get("result", {}).get("access_token")',
      '    if not token:',
      '        raise ValueError("access_token not found in response")',
      '    return token',
      '',
      '',
      'if __name__ == "__main__":',
      '    token = get_access_token()',
      '    q = urllib.parse.quote(f"id=={ENTITY_ID}", safe="")',
      '    url = f"{RS_BASE_URL}?id={ENTITY_ID}&q={q}"',
      '    headers = {"authorization": f"Bearer {token}"}',
      '    resp = requests.get(url, headers=headers, timeout=TIMEOUT)',
      '    resp.raise_for_status()',
      '    out = resp.json()',
      '    with open("gateway-response.json", "w", encoding="utf-8") as f:',
      '        json.dump(out, f, indent=2)',
      '    print("Saved gateway-response.json")',
    ].join('\n');
  }
}
